import React, { useEffect } from 'react'
import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react'
import { isMobileWindow } from '@helpers/deviceWindow'
import { getCurrentLocation } from '@helpers/geo-location'
import PropTypes from 'prop-types'
import { addToDataLayer_nextgen } from '@helpers/google-tag-manager'
import { isEmpty } from 'lodash'
import { getSessionId } from '@helpers/cloudfront'
import { getFromBrowserStorage } from '@helpers/storage'
import { format } from 'date-fns'

const growthbook = new GrowthBook({
  apiHost: `${process.env.GATSBY_GROWTHBOOK_API_HOST}`,
  clientKey: `${process.env.GATSBY_GROWTHBOOK_CLIENT_KEY}`,
  enableDevMode: true,
  trackingCallback: (experiment, result) => {
    addToDataLayer_nextgen('experiment_tracking', {
      experimentId: experiment.key,
      variationId: result.key,
    })
  },
  onFeatureUsage: (featureKey, result) => {
    if (result?.value === true || !isEmpty(result?.value)) {
      addToDataLayer_nextgen('feature_usage', {
        featureKey,
        value: result.value,
      })
    }
  },
})

const setAttributes = () => {
  try {
    // eslint-disable-next-line no-undef
    const browser = navigator?.userAgent?.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i)?.[1]
    const { pathname: path, href: url } = window.location
    const referrer = document?.referrer
    const location = getCurrentLocation()
    const mobile = isMobileWindow()
    const tags = getUpdatedTags()
    const date = format(new Date(), 'MM-dd-yyyy')

    const gbContext = {
      ...growthbook.getAttributes(),
      id: getSessionId(),
      browser,
      url: url?.toLowerCase(),
      referrer,
      path,
      ...location,
      mobile,
      tags,
      date,
      rtgUserContext: getRtgUserContext(),
    }
    growthbook.setAttributes(gbContext)
  } catch (error) {
    console.error(`Failed to set growthbook attributes: ${error.message}`)
  }
}

// update a single Growthbook attribute
export const updateAttribute = (attribute, value) => {
  const attributes = growthbook.getAttributes()
  growthbook.setAttributes({
    ...attributes,
    [attribute]: value,
  })
}

// Stores current modal in local storage temporarily
export const storeGrowthbookModalInLs = tag => {
  localStorage.setItem('current-modal', tag)
}

// Gets the current rtgUserContext from local storage
const getRtgUserContext = () => {
  const rtgUserContext = getFromBrowserStorage('local', 'rtgUserContext')
  if (typeof rtgUserContext === 'object' && rtgUserContext !== null && Object.keys(rtgUserContext).length > 0) {
    return rtgUserContext
  }
  return {}
}

// First updates growthbook-tags with the latest opened modal
// Then returns an array of updated tags
const getUpdatedTags = () => {
  const persistedTags = localStorage.getItem('growthbook-tags')
  const tags = persistedTags ? JSON.parse(persistedTags) : []
  const modalSeenTag = 'personalized-modal-seen'

  const currentModalTag = localStorage.getItem('current-modal')
  if (currentModalTag && !tags.includes(currentModalTag)) {
    tags.push(currentModalTag)
    if (!tags.includes(modalSeenTag)) tags.push(modalSeenTag)
    localStorage.setItem('growthbook-tags', JSON.stringify(tags))
  }
  return tags
}

// Updates growthbook tag attributes and local storage
export const addGrowthbookTag = tag => {
  const attributes = growthbook.getAttributes()
  attributes.tags = attributes.tags ?? []
  if (!attributes.tags.includes(tag)) {
    attributes.tags.push(tag)
    growthbook.setAttributes(attributes)
    localStorage.setItem('growthbook-tags', JSON.stringify(attributes.tags))
  }
}

const GrowthbookComponent = ({ children }) => {
  useEffect(() => {
    growthbook.loadFeatures()
    setAttributes()
  }, [])

  return <GrowthBookProvider growthbook={growthbook}>{children}</GrowthBookProvider>
}

export default GrowthbookComponent

GrowthbookComponent.propTypes = {
  children: PropTypes.node,
}
